/**
 * Goods Transfer Request List Component
 * Display and manage all goods transfer requests
 */

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useInventory } from '../../../contexts/InventoryContext';
import adminApi from '../../../services/adminApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import PermissionGuard from '../../Common/PermissionGuard';
import ConfirmationModal from '../../Common/ConfirmationModal';
import GTRForm from './GTRForm';
import GTRViewModal from './GTRViewModal';

const GTRList = () => {
    const [showForm, setShowForm] = useState(false);
    const [selectedGTR, setSelectedGTR] = useState(null);
    const [viewModalGTRId, setViewModalGTRId] = useState(null);
    const { goodsTransferRequests, loading, fetchGoodsTransferRequests, cancelGoodsTransferRequest, getGTRToIssueCount } = useInventory();
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [warehouseFilter, setWarehouseFilter] = useState('');
    // 'requested' = transfers I've requested (default); 'toIssue' = approved transfers
    // where my location is the SOURCE, awaiting a Goods Issue Note
    const [perspective, setPerspective] = useState('requested');
    // Count of approved GTRs at my location still awaiting a Goods Issue Note — shown
    // as a badge on the "To Issue" tab regardless of which tab is currently active.
    const [toIssueCount, setToIssueCount] = useState(0);
    const [error, setError] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const searchTimeoutRef = useRef(null);
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    // requestedBy is a raw user id (not populated — see requestedByName comment on the
    // GoodsTransferRequest model). Resolve older records missing requestedByName in one
    // batched lookup via the auth-service's no-elevated-permission-required endpoint.
    const [requestedByMap, setRequestedByMap] = useState({});

    const [view, setView] = useState(() => {
        try {
            return localStorage.getItem('gtrListView') || 'card';
        } catch {
            return 'card';
        }
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    // Debounce search input
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchInput]);

    useEffect(() => {
        loadGoodsTransferRequests();
    }, [debouncedSearchInput, statusFilter, priorityFilter, warehouseFilter, perspective, pagination.page, pagination.limit]);

    const loadToIssueCount = async () => {
        try {
            const result = await getGTRToIssueCount();
            setToIssueCount(result?.data?.count || 0);
        } catch (err) {
            console.error('Error loading to-issue count:', err);
        }
    };

    useEffect(() => {
        const idsNeedingResolution = [...new Set(
            (goodsTransferRequests || [])
                .filter(gtr => !gtr.requestedByName && typeof gtr.requestedBy === 'string')
                .map(gtr => gtr.requestedBy)
        )].filter(id => !requestedByMap[id]);

        if (idsNeedingResolution.length === 0) return;

        adminApi.getAssignableUsers({ ids: idsNeedingResolution.join(',') })
            .then(response => {
                const users = response.data?.data?.users || [];
                if (users.length === 0) return;
                setRequestedByMap(prev => {
                    const next = { ...prev };
                    users.forEach(u => { next[u._id] = u; });
                    return next;
                });
            })
            .catch(err => console.error('Error resolving requestedBy users:', err));
    }, [goodsTransferRequests]);

    const getRequestedByDisplay = (gtr) => {
        if (gtr.requestedByName) return gtr.requestedByName;
        const user = typeof gtr.requestedBy === 'string' ? requestedByMap[gtr.requestedBy] : null;
        if (user) {
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            return fullName || user.email || 'Unknown';
        }
        return 'Unknown';
    };

    const handlePerspectiveChange = (newPerspective) => {
        setPerspective(newPerspective);
        // Reset the status filter so a stale selection from the other tab can't carry
        // over — the backend also enforces this, but the dropdown should match reality.
        setStatusFilter('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleAddGTR = () => {
        setSelectedGTR(null);
        setShowForm(true);
    };

    const handleEditGTR = (gtr) => {
        setSelectedGTR(gtr);
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setSelectedGTR(null);
    };

    const handleFormSuccess = () => {
        setShowForm(false);
        setSelectedGTR(null);
        loadGoodsTransferRequests();
    };

    const handleViewGTR = (gtrId) => {
        setViewModalGTRId(gtrId);
    };

    const handleCloseViewModal = () => {
        setViewModalGTRId(null);
    };

    const handleViewModalSuccess = () => {
        loadGoodsTransferRequests(); // Refresh list after approval/rejection
    };

    const handleCancelGTR = async (gtr) => {
        setConfirmAction({
            type: 'danger',
            title: 'Cancel Transfer Request',
            message: `Are you sure you want to cancel "${gtr.requestNo}"? This action cannot be undone.`,
            confirmText: 'Cancel Request',
            onConfirm: async () => {
                try {
                    await cancelGoodsTransferRequest(gtr._id, 'Cancelled by user');
                    loadGoodsTransferRequests();
                } catch (err) {
                    console.error('Cancel GTR error:', err);
                }
            }
        });
    };

    const loadGoodsTransferRequests = async () => {
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                perspective
            };

            if (debouncedSearchInput) params.search = debouncedSearchInput;
            if (statusFilter) params.status = statusFilter;
            if (priorityFilter) params.priority = priorityFilter;
            if (warehouseFilter) params.sourceWarehouse = warehouseFilter;

            const result = await fetchGoodsTransferRequests({ ...params, sortBy: 'createdAt', sortOrder: 'desc' });

            // Update pagination from API response
            if (result?.pagination) {
                setPagination({
                    page: result.pagination.page || 1,
                    limit: result.pagination.limit || 20,
                    total: result.pagination.total || 0,
                    pages: result.pagination.pages || 0
                });
            }

        } catch (err) {
            console.error('Error loading GTRs:', err);
            toast.error(err.response?.data?.message || err.message || 'Failed to load goods transfer requests');
        }

        loadToIssueCount();
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => {
            return { ...prev, page: newPage };
        });
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ page: 1, limit: newLimit, total: prev.total, pages: prev.pages }));
    };

    const handleViewChange = (newView) => {
        setView(newView);
        try {
            localStorage.setItem('gtrListView', newView);
        } catch (error) {
            console.warn('Failed to save view preference:', error);
        }
    };

    const clearFilters = () => {
        setSearchInput('');
        setStatusFilter('');
        setPriorityFilter('');
        setWarehouseFilter('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasFilters = () => {
        return searchInput || statusFilter || priorityFilter || warehouseFilter;
    };

    const filteredGTRs = goodsTransferRequests || [];

    // Status badge styling
    const getStatusBadgeColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'draft':
                return 'bg-gray-100 text-gray-700 border-gray-300';
            case 'pending':
                return 'bg-orange-100 text-orange-700 border-orange-300';
            case 'requested':
                return 'bg-yellow-100 text-yellow-700 border-yellow-300';
            case 'approved':
                return 'bg-green-100 text-green-700 border-green-300';
            case 'rejected':
                return 'bg-red-100 text-red-700 border-red-300';
            case 'cancelled':
                return 'bg-gray-100 text-gray-500 border-gray-300';
            case 'converted_to_gi':
                return 'bg-indigo-100 text-indigo-700 border-indigo-300';
            case 'partially_fulfilled':
                return 'bg-blue-100 text-blue-700 border-blue-300';
            case 'fulfilled':
                return 'bg-emerald-100 text-emerald-700 border-emerald-300';
            default:
                return 'bg-gray-100 text-gray-600 border-gray-300';
        }
    };

    const getPriorityBadgeColor = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'urgent':
                return 'bg-red-100 text-red-700 border-red-300';
            case 'normal':
                return 'bg-gray-100 text-gray-700 border-gray-300';
            default:
                return 'bg-gray-100 text-gray-600 border-gray-300';
        }
    };

    // On the "To Issue" tab the source location can only ever see requests that have
    // already been approved — the backend enforces this regardless, but the dropdown
    // shouldn't offer statuses that will never return anything there.
    const statusOptions = perspective === 'toIssue' ? [
        { value: '', label: 'All (Approved+)' },
        { value: 'approved', label: 'Approved' },
        { value: 'converted_to_gi', label: 'Converted (Issue Note Raised)' },
        { value: 'partially_fulfilled', label: 'Partially Fulfilled' },
        { value: 'fulfilled', label: 'Fulfilled' }
    ] : [
        { value: '', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'requested', label: 'Requested' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'converted_to_gi', label: 'Converted (Issue Note Raised)' },
        { value: 'partially_fulfilled', label: 'Partially Fulfilled' },
        { value: 'fulfilled', label: 'Fulfilled' }
    ];

    const priorityOptions = [
        { value: '', label: 'All Priorities' },
        { value: 'normal', label: 'Normal' },
        { value: 'urgent', label: 'Urgent' }
    ];

    if (loading && !goodsTransferRequests) {
        return <LoadingSpinner />;
    }

    const renderGTRCard = (gtr) => (
        <div key={gtr._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group w-full">
            <div className="p-3 flex flex-col gap-2">

                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${gtr.priority === 'urgent' ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-indigo-500 to-purple-500'}`}>
                            {gtr.priority?.charAt(0)?.toUpperCase() || 'N'}
                        </div>
                        <div className="min-w-0 flex-1">
                            <button
                                onClick={() => handleViewGTR(gtr._id)}
                                className="text-sm font-semibold text-gray-900 hover:text-indigo-600 transition-colors truncate text-left block"
                            >
                                {gtr.requestNo}
                            </button>
                            <p className="text-xs text-gray-400">{new Date(gtr.requestDate).toLocaleDateString()}</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                            onClick={() => handleViewGTR(gtr._id)}
                            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                            title="View request"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </button>
                        {(['draft', 'pending', 'requested'].includes(gtr.status)) && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleEditGTR(gtr);
                                }}
                                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                title="Edit request"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </button>
                        )}
                        {(['draft', 'pending', 'requested'].includes(gtr.status)) && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleCancelGTR(gtr);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                title="Cancel request"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Route row */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="truncate">{gtr.sourceWarehouseName}</span>
                    <svg className="w-3 h-3 mx-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="truncate">{gtr.destinationWarehouseName}</span>
                </div>

                {/* Items + Status */}
                <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-600 truncate">
                        {gtr.lineItems?.length || 0} items • {getRequestedByDisplay(gtr)}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border flex-shrink-0 ml-2 ${getStatusBadgeColor(gtr.status)}`}>
                        {gtr.status}
                    </span>
                </div>

            </div>
        </div>
    );

    const renderGTRRow = (gtr) => (
        <tr key={gtr._id} className="hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${gtr.priority === 'urgent' ? 'bg-gradient-to-br from-red-100 to-red-200 text-red-700' : 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700'}`}>
                        {gtr.priority?.charAt(0)?.toUpperCase() || 'N'}
                    </div>
                    <div>
                        <button
                            onClick={() => handleViewGTR(gtr._id)}
                            className="text-sm font-semibold text-gray-900 hover:text-indigo-600"
                        >
                            {gtr.requestNo}
                        </button>
                        <div className="text-xs text-gray-400">
                            {new Date(gtr.requestDate).toLocaleDateString()}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">{gtr.sourceWarehouseName}</div>
                <div className="text-sm text-gray-900">{gtr.destinationWarehouseName}</div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(gtr.status)}`}>
                    {gtr.status}
                </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getPriorityBadgeColor(gtr.priority)}`}>
                    {gtr.priority}
                </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                {gtr.lineItems?.length || 0} items
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                {getRequestedByDisplay(gtr)}
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                    <button
                        onClick={() => handleViewGTR(gtr._id)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                        title="View request"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </button>
                    {(['draft', 'pending', 'requested'].includes(gtr.status)) && (
                        <button
                            onClick={() => handleEditGTR(gtr)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                            title="Edit request"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </button>
                    )}
                    {(['draft', 'pending', 'requested'].includes(gtr.status)) && (
                        <button
                            onClick={() => handleCancelGTR(gtr)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                            title="Cancel request"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );

    return (
        <div className="bg-white shadow-sm border border-gray-200">
            {/* Error Display */}
            {error && (
                <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
                    <div className="flex items-center">
                        <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-medium text-red-800">{error}</p>
                        <button
                            onClick={() => setError('')}
                            className="ml-auto text-red-600 hover:text-red-500"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
 

            {/* Perspective Tabs */}
            <div className="px-4 sm:px-6 lg:px-8 pt-4 flex-shrink-0">
                <div className="flex items-center gap-1 border-b border-gray-200">
                    <button
                        onClick={() => handlePerspectiveChange('requested')}
                        className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${perspective === 'requested'
                            ? 'border-indigo-600 text-indigo-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        My Requests
                    </button>
                    <button
                        onClick={() => handlePerspectiveChange('toIssue')}
                        className={`relative px-3 py-2 text-sm font-medium border-b-2 transition-colors ${perspective === 'toIssue'
                            ? 'border-indigo-600 text-indigo-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        To Issue
                        {toIssueCount > 0 && (
                            <span
                                className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none"
                                title={`${toIssueCount} transfer request${toIssueCount === 1 ? '' : 's'} not yet converted to a Goods Issue Note`}
                            >
                                {toIssueCount > 99 ? '99+' : toIssueCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                    {/* Search */}
                    <div className="flex-1">
                        <TextInput
                            variant="input"
                            type="text"
                            placeholder="Search by request number, items..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            icon={({ className }) => (
                                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            )}
                            className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                        />
                    </div>

                    {/* Filters and Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <CustomSelect
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            options={statusOptions}
                            size="sm"
                            variant="default"
                            className="min-w-[150px]"
                        />
                        <CustomSelect
                            value={priorityFilter}
                            onChange={(e) => setPriorityFilter(e.target.value)}
                            options={priorityOptions}
                            size="sm"
                            variant="default"
                            className="min-w-[120px]"
                        />

                        {/* Reset Filters */}
                        {hasFilters() && (
                            <button
                                onClick={clearFilters}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                title="Clear all filters"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}

                        {/* View Toggle */}
                        <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                            <button
                                onClick={() => handleViewChange('card')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                title="Card view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                                </svg>
                                Cards
                            </button>
                            <button
                                onClick={() => handleViewChange('table')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'table' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                title="List view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                                List
                            </button>
                        </div>

                        {/* Add New GTR Button */}
                        <PermissionGuard permission="INVENTORY:STOCK_TRANSFERS:CREATE">
                            <button
                                onClick={handleAddGTR}
                                className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                            >
                                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                New Transfer Request
                            </button>
                        </PermissionGuard>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <div className="flex items-center justify-between px-2 py-2 bg-gradient-to-r from-gray-100 to-gray-100 border-b border-gray-200">
                    {/* Results Info */}
                    <div className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-900">
                                {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} transfer requests
                            </p>
                            <p className="text-[10px] text-gray-500">
                                {hasFilters() ? 'Filtered results' : 'All transfer requests'}
                            </p>
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center gap-3">
                        {/* Items per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 font-medium">Show:</span>
                            <CustomSelect
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                                disabled={loading}
                                options={[
                                    { value: 10, label: '10' },
                                    { value: 20, label: '20' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                size="sm"
                                variant="minimal"
                                className="min-w-[70px]"
                            />
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.page <= 1 || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="First page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                            </button>

                            <button
                                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                                disabled={pagination.page <= 1 || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className="hidden sm:inline">Previous</span>
                            </button>

                            {/* Page Numbers */}
                            <div className="hidden md:flex items-center gap-1">
                                {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                                    const pageNum = i + 1;
                                    const isCurrentPage = pageNum === pagination.page;

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            disabled={loading}
                                            className={`min-w-[28px] px-2 py-1.5 rounded text-xs font-medium transition-all ${isCurrentPage
                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                {pagination.pages > 7 && (
                                    <span className="px-2 text-gray-400 text-xs">...</span>
                                )}
                            </div>

                            {/* Mobile page indicator */}
                            <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                                <span className="text-xs font-medium text-gray-900">{pagination.page}</span>
                                <span className="text-xs text-gray-500 mx-1">of</span>
                                <span className="text-xs font-medium text-gray-900">{pagination.pages}</span>
                            </div>

                            <button
                                onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <span className="hidden sm:inline">Next</span>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <button
                                onClick={() => handlePageChange(pagination.pages)}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="Last page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Area */}
            {view === 'card' ? (
                <div className="px-4 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {filteredGTRs.length === 0 ? (
                            <div className="col-span-full bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                <h3 className="text-sm font-medium text-gray-900 mb-2">No transfer requests found</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    {hasFilters() ? 'Try adjusting your search or filters' : 'Get started by creating your first transfer request'}
                                </p>
                                {!hasFilters() && (
                                    <PermissionGuard permission="INVENTORY:STOCK_TRANSFERS:CREATE">
                                        <button
                                            onClick={handleAddGTR}
                                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                            New Transfer Request
                                        </button>
                                    </PermissionGuard>
                                )}
                            </div>
                        ) : (
                            filteredGTRs.map(renderGTRCard)
                        )}
                    </div>
                </div>
            ) : (
                <div className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-max w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Request</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Route</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Priority</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Items</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Requested By</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredGTRs.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-4 py-12 text-center">
                                            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                            </svg>
                                            <h3 className="text-sm font-medium text-gray-900 mb-2">No transfer requests found</h3>
                                            <p className="text-sm text-gray-500">
                                                {hasFilters() ? 'Try adjusting your search or filters' : 'Get started by creating your first transfer request'}
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredGTRs.map(renderGTRRow)
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* GTR Form Modal */}
            {showForm && (
                <GTRForm
                    gtr={selectedGTR}
                    onSuccess={handleFormSuccess}
                    onCancel={handleFormClose}
                />
            )}

            {/* GTR View Modal */}
            {viewModalGTRId && (
                <GTRViewModal
                    gtrId={viewModalGTRId}
                    onClose={handleCloseViewModal}
                    onSuccess={handleViewModalSuccess}
                />
            )}

            {/* Confirmation Modal */}
            {confirmAction && (
                <ConfirmationModal
                    isOpen={!!confirmAction}
                    onClose={() => setConfirmAction(null)}
                    onConfirm={confirmAction.onConfirm}
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmText={confirmAction.confirmText}
                    type={confirmAction.type}
                />
            )}
        </div>
    );
};

export default GTRList;